/**
 * Shared configs for Admin Taxonomies hierarchy builders.
 * All types use entities.parent + /hierarchy-nodes with the same UI features.
 */

const buildConfig = ({
  id,
  entityType,
  idPrefix,
  entityLabel,
  entityLabelLower,
  entityLabelPlural,
  navLabel,
  title,
  subtitle,
  shortDesc,
}) => ({
  id,
  entityType,
  idPrefix,
  entityLabel,
  entityLabelLower,
  entityLabelPlural,
  navLabel,
  title,
  subtitle,
  shortDesc,
  newButtonLabel: `New ${entityLabel}`,
  searchPlaceholder: `Search ${entityLabelPlural}...`,
  allLabel: `All ${entityLabelPlural} (full tree)`,
  loadingLabel: `Loading ${entityLabelPlural}...`,
  emptyLabel: `No ${entityLabelPlural} found`,
  emptySearchLabel: `No ${entityLabelPlural} found matching your search`,
  emptyHint: `Create your first ${entityLabelLower} to get started`,
  createFirstLabel: `Create first ${entityLabelLower}`,
  createPanelTitle: `Create new ${entityLabelLower}`,
  createPanelSubtitle: `Add a new ${entityLabelLower} to your hierarchy`,
  nameLabel: `${entityLabel} name`,
  parentLabel: `Parent ${entityLabelLower}`,
  createActionLabel: `Create ${entityLabelLower}`,
  updateSuccessLabel: `${entityLabel} updated successfully.`,
  deleteSuccessLabel: `${entityLabel} deleted successfully.`,
});

export const CAPABILITY_HIERARCHY_CONFIG = buildConfig({
  id: "capabilities",
  entityType: "business-capability",
  idPrefix: "cap_",
  entityLabel: "Capability",
  entityLabelLower: "capability",
  entityLabelPlural: "capabilities",
  navLabel: "Capabilities",
  title: "Business capability hierarchy",
  subtitle:
    "Organize business capabilities in a parent–child tree for maps, reports, and the assistant.",
  shortDesc: "What the business does, structured as a capability map.",
});

export const PROCESS_HIERARCHY_CONFIG = buildConfig({
  id: "processes",
  entityType: "business-context",
  idPrefix: "proc_",
  entityLabel: "Process",
  entityLabelLower: "process",
  entityLabelPlural: "processes",
  navLabel: "Processes",
  title: "Business process hierarchy",
  subtitle:
    "Organize business processes in a hierarchical structure (APQC-style imports supported).",
  shortDesc: "How work gets done—process trees and APQC-style import.",
});

export const ORGANISATION_HIERARCHY_CONFIG = buildConfig({
  id: "organisation",
  entityType: "organization",
  idPrefix: "org_",
  entityLabel: "Organisation",
  entityLabelLower: "organisation",
  entityLabelPlural: "organisations",
  navLabel: "Organisation",
  title: "Organisation hierarchy",
  subtitle:
    "Model teams, departments, and business units in a reporting structure.",
  shortDesc: "Teams, departments, and business units.",
});

export const OBJECTIVE_HIERARCHY_CONFIG = buildConfig({
  id: "objectives",
  entityType: "objective",
  idPrefix: "obj_",
  entityLabel: "Objective",
  entityLabelLower: "objective",
  entityLabelPlural: "objectives",
  navLabel: "Objectives",
  title: "Objective hierarchy",
  subtitle:
    "Break goals into nested objectives so strategy stays connected to delivery.",
  shortDesc: "Goals and outcomes nested from strategy to detail.",
});

export const INITIATIVE_HIERARCHY_CONFIG = buildConfig({
  id: "initiatives",
  entityType: "initiative",
  idPrefix: "init_",
  entityLabel: "Initiative",
  entityLabelLower: "initiative",
  entityLabelPlural: "initiatives",
  navLabel: "Initiatives",
  title: "Initiative hierarchy",
  subtitle:
    "Structure programmes and projects so change work rolls up cleanly.",
  shortDesc: "Programmes and projects in a delivery tree.",
});

/** Ordered list for the Taxonomies switcher (same business narrative as Your map). */
export const TAXONOMY_HIERARCHY_CONFIGS = [
  ORGANISATION_HIERARCHY_CONFIG,
  OBJECTIVE_HIERARCHY_CONFIG,
  INITIATIVE_HIERARCHY_CONFIG,
  PROCESS_HIERARCHY_CONFIG,
  CAPABILITY_HIERARCHY_CONFIG,
];

export const TAXONOMY_HIERARCHY_TYPE_SET = new Set(
  TAXONOMY_HIERARCHY_CONFIGS.map((c) => c.entityType)
);

/** Normalize type strings like "Business Context" / business_context → business-context. */
export const normalizeTaxonomyEntityType = (type) =>
  String(type || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

export const isTaxonomyHierarchyType = (type) =>
  TAXONOMY_HIERARCHY_TYPE_SET.has(normalizeTaxonomyEntityType(type));

export const getTaxonomyHierarchyConfig = (id) =>
  TAXONOMY_HIERARCHY_CONFIGS.find((c) => c.id === id) ||
  ORGANISATION_HIERARCHY_CONFIG;

export const getTaxonomyHierarchyConfigByEntityType = (type) => {
  const norm = normalizeTaxonomyEntityType(type);
  return (
    TAXONOMY_HIERARCHY_CONFIGS.find((c) => c.entityType === norm) || null
  );
};
